import React, { useState, useEffect } from 'react';
import { Plus, X, Zap, Users, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import type { AutoEnrollmentConfig, EnrollmentCondition } from '@/actions/drip-campaigns';
import { fetchLeadColumns } from '@/actions/lead-manager';

interface ColumnOption {
  opt_id: number;
  opt_name: string;
  opt_active: number | boolean; // Can be 1/0 or true/false
}

interface LeadColumn {
  col_id?: number;
  col_name: string;
  col_label?: string;
  col_type: string;
  col_active?: number | boolean;
  options?: ColumnOption[];
}

interface EnrollmentSettingsProps {
  config: AutoEnrollmentConfig | null;
  onChange: (config: AutoEnrollmentConfig | null) => void;
}

const EnrollmentSettings: React.FC<EnrollmentSettingsProps> = ({ config, onChange }) => {
  // Auto-enrollment is always enabled - no manual option
  const [triggerType, setTriggerType] = useState<'new_lead' | 'status_change'>(
    (config?.trigger_type as 'new_lead' | 'status_change') || 'new_lead'
  );
  const [conditions, setConditions] = useState<EnrollmentCondition[]>(config?.conditions || []);
  const [logic, setLogic] = useState<'AND' | 'OR'>(config?.logic || 'AND');
  const [isCollapsed, setIsCollapsed] = useState(false); // Start expanded for better visibility
  const [availableFields, setAvailableFields] = useState<Array<{ value: string; label: string; type: string; options?: ColumnOption[] }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' }
  ];

  // Sync state when config prop changes (e.g., when editing a campaign)
  useEffect(() => {
    if (config) {
      console.log('EnrollmentSettings: Received config:', config);
      setTriggerType((config.trigger_type as 'new_lead' | 'status_change') || 'new_lead');
      setConditions(config.conditions || []);
      setLogic(config.logic || 'AND');
    }
  }, [config]);

  // Fetch dynamic columns from API
  useEffect(() => {
    const loadColumns = async () => {
      try {
        setIsLoading(true);
        const response = await fetchLeadColumns();

        console.log('Columns API Response:', response);

        if (response.success && response.columns) {
          console.log('Raw columns data:', response.columns);

          const fields = response.columns
            .filter((col: LeadColumn) => {
              // Backend already filters by active=true when we pass active=true
              // So we don't need to filter again, unless col_active is explicitly false
              const isActive = col.col_active !== false && col.col_active !== 0;
              console.log(`Column ${col.col_name}: col_active=${col.col_active}, isActive=${isActive}`);
              return isActive;
            })
            .map((col: LeadColumn) => {
              // Filter active options for select-type columns
              // Backend returns options in the 'options' field
              // opt_active can be boolean (true/false) or number (1/0)
              const activeOptions = col.options
                ? col.options.filter(opt => opt.opt_active === true || opt.opt_active === 1)
                : [];

              console.log(`Column ${col.col_name}: type=${col.col_type}, label=${col.col_label}, options count=${activeOptions.length}`);

              return {
                value: col.col_name,
                label: col.col_label || col.col_name,
                type: col.col_type,
                options: activeOptions.length > 0 ? activeOptions : undefined
              };
            });

          console.log('Processed fields:', fields);
          console.log('Setting availableFields state with', fields.length, 'fields');
          setAvailableFields(fields);

          // Log after a short delay to confirm state was set
          setTimeout(() => {
            console.log('availableFields state check:', fields.length, 'items');
          }, 100);
        } else {
          console.error('No columns data in response:', response);
        }
      } catch (error) {
        console.error('Error loading columns:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadColumns();
  }, []);

  const handleTriggerTypeChange = (type: 'new_lead' | 'status_change') => {
    setTriggerType(type);
    updateConfig({ trigger_type: type });
  };

  const addCondition = () => {
    const newCondition: EnrollmentCondition = {
      field: availableFields.length > 0 ? availableFields[0].value : '',
      operator: 'equals',
      value: ''
    };
    const newConditions = [...conditions, newCondition];
    setConditions(newConditions);
    updateConfig({ conditions: newConditions });
  };

  // Get field details by value
  const getFieldByValue = (value: string) => {
    return availableFields.find(f => f.value === value);
  };

  const updateCondition = (index: number, updates: Partial<EnrollmentCondition>) => {
    const newConditions = conditions.map((cond, i) =>
      i === index ? { ...cond, ...updates } : cond
    );
    setConditions(newConditions);
    updateConfig({ conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
    updateConfig({ conditions: newConditions });
  };

  const updateLogic = (newLogic: 'AND' | 'OR') => {
    setLogic(newLogic);
    updateConfig({ logic: newLogic });
  };

  const updateConfig = (updates: Partial<AutoEnrollmentConfig>) => {
    const newConfig: AutoEnrollmentConfig = {
      enabled: true, // Always enabled - no manual option
      trigger_type: triggerType,
      conditions: conditions,
      logic: logic,
      ...updates
    };
    onChange(newConfig);
  };

  // Generate summary text for collapsed view
  const getSummaryText = () => {
    const triggerText = triggerType === 'new_lead' ? 'New Lead Created' : 'Status Change';
    const conditionsText = conditions.length > 0 ? ` • ${conditions.length} condition${conditions.length > 1 ? 's' : ''}` : '';
    return `Trigger: ${triggerText}${conditionsText}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
      {/* Collapsible Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3 flex-1">
          <Zap className="w-5 h-5 text-brandColor" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Enrollment Trigger
            </h3>
            {isCollapsed && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {getSummaryText()}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed(!isCollapsed);
          }}
        >
          {isCollapsed ? (
            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Expandable Content */}
      {!isCollapsed && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-600 dark:text-gray-400">Loading columns...</div>
            </div>
          ) : (
            <div className="space-y-6">
                  {/* Trigger Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      When should this campaign start?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleTriggerTypeChange('new_lead')}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          triggerType === 'new_lead'
                            ? 'border-brandColor bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Users className="w-5 h-5 text-brandColor flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">New Lead Created</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Enroll leads automatically when they're created
                            </div>
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleTriggerTypeChange('status_change')}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          triggerType === 'status_change'
                            ? 'border-brandColor bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Filter className="w-5 h-5 text-brandColor flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">Status Changes</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Enroll when lead status changes to specific value
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Conditions */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enrollment Conditions
                        {conditions.length > 1 && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({logic === 'AND' ? 'All must match' : 'Any can match'})
                          </span>
                        )}
                      </label>
                      <button
                        type="button"
                        onClick={addCondition}
                        className="flex items-center gap-1 px-3 py-1 text-xs text-brandColor border border-brandColor rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Add Condition
                      </button>
                    </div>

                    {conditions.length === 0 ? (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          No conditions set. All {triggerType === 'new_lead' ? 'new leads' : 'status changes'} will trigger this campaign.
                        </p>
                        <button
                          type="button"
                          onClick={addCondition}
                          className="mt-2 text-sm text-brandColor hover:underline"
                        >
                          Add a condition to filter leads
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {conditions.map((condition, index) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            {index > 0 && (
                              <div className="flex items-center gap-2 mr-2">
                                <select
                                  value={logic}
                                  onChange={(e) => updateLogic(e.target.value as 'AND' | 'OR')}
                                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                  <option value="AND">AND</option>
                                  <option value="OR">OR</option>
                                </select>
                              </div>
                            )}

                            <select
                              value={condition.field}
                              onChange={(e) => {
                                // Reset value when field changes to avoid mismatched values
                                updateCondition(index, { field: e.target.value, value: '' });
                              }}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              {availableFields.length === 0 && (
                                <option value="">Loading columns...</option>
                              )}
                              {availableFields.map(field => (
                                <option key={field.value} value={field.value}>
                                  {field.label}
                                </option>
                              ))}
                            </select>

                            <select
                              value={condition.operator}
                              onChange={(e) => updateCondition(index, { operator: e.target.value as any })}
                              className="w-32 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              {operators.map(op => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </select>

                            {(() => {
                              const field = getFieldByValue(condition.field);

                              // Render dropdown for select-type columns with options
                              if (field?.options && field.options.length > 0) {
                                return (
                                  <select
                                    value={condition.value}
                                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    <option value="">Select {field.label.toLowerCase()}...</option>
                                    {field.options.map(opt => (
                                      <option key={opt.opt_id} value={opt.opt_name}>
                                        {opt.opt_name}
                                      </option>
                                    ))}
                                  </select>
                                );
                              }
                              // Render text input for other fields
                              return (
                                <input
                                  type="text"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(index, { value: e.target.value })}
                                  placeholder="Enter value..."
                                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              );
                            })()}

                            <button
                              type="button"
                              onClick={() => removeCondition(index)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Remove condition"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Help Text */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <span className="font-medium">💡 How it works:</span>{' '}
                      {triggerType === 'new_lead' ? (
                        <>
                          When a new lead is created{conditions.length > 0 ? ' and matches your conditions' : ''},
                          they will be automatically enrolled into this campaign and the first step will execute.
                        </>
                      ) : (
                        <>
                          When a lead's status changes{conditions.length > 0 ? ' and matches your conditions' : ''},
                          they will be automatically enrolled into this campaign.
                        </>
                      )}
                    </p>
                    {conditions.length > 1 && (
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                        Using <span className="font-medium">{logic}</span> logic: {logic === 'AND' ? 'All' : 'Any'} conditions must match for enrollment.
                      </p>
                    )}
                  </div>

                  {/* Example */}
                  {conditions.length > 0 && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Example Enrollment:
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {triggerType === 'new_lead' ? 'A new lead is created' : 'Lead status changes'}
                        {conditions.map((cond, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && ` ${logic.toLowerCase()} `}
                            {i === 0 && ' where '}
                            <span className="font-medium text-gray-900 dark:text-white">
                              {availableFields.find(f => f.value === cond.field)?.label || cond.field}
                            </span>
                            {' '}
                            <span className="text-gray-500">{cond.operator.replace('_', ' ')}</span>
                            {' '}
                            <span className="font-medium text-brandColor">"{cond.value || '(empty)'}"</span>
                          </React.Fragment>
                        ))}
                        {' → Lead enrolls into this campaign automatically ✅'}
                      </p>
                    </div>
                  )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnrollmentSettings;
